import React, { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  Box, 
  Avatar, 
  CircularProgress, 
  TextField, 
  Autocomplete,
  Button,
  createTheme,
  ThemeProvider
} from "@mui/material";
import { Camera } from "lucide-react";
import { signUpAgent } from "../../services/agent_management_service";
import { 
  getCoutries, 
  getProvincesBaseOnCountry, 
  getDistrictsBaseOnProvince 
} from "../../services/public_service";
import { useQuery } from "@tanstack/react-query";
import StyledForm from "../ui/formUI/StyledForm";
import FormSection from "../ui/formUI/FormSection";
import SectionTitle from "../ui/formUI/SectionTitle";
import { useTranslation } from "react-i18next";

const isDarkModeActive = () => {
  if (typeof window !== 'undefined') {
    return document.documentElement.classList.contains('dark');
  }
  return false;
};

const languages = [
  { label: "English", value: "english", icon: "https://flagcdn.com/us.svg" },
  { label: "Dari", value: "dari", icon: "https://flagcdn.com/af.svg" },
  { label: "Pashto", value: "pashto", icon: "https://flagcdn.com/af.svg" },
];

// Custom TextField component that properly handles MUI dark mode
const CustomTextField = ({ isDark, ...props }) => {
  return (
    <TextField
      {...props}
      sx={{
        width: "100%",
        borderRadius: "20px",
        "& .MuiOutlinedInput-root": {
          paddingY: "0 !important",
          height: "46px",
          "& .MuiAutocomplete-input": { padding: "0 !important" },
          "&.Mui-focused fieldset": { borderColor: "#CD0C02 !important" },
          "&:hover fieldset": { borderColor: "#ff6666" },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#4b5563" : "#e5e7eb",
          },
          backgroundColor: isDark ? "#1f2937" : "#ffffff",
        },
        "& .MuiInputLabel-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
          "&.Mui-focused": { color: "#CD0C02 !important" },
        },
        "& .MuiInputBase-input": {
          color: isDark ? "#f3f4f6" : "#1f2937",
        },
        "& .MuiFormHelperText-root": {
          color: isDark ? "#9ca3af" : "#6b7280",
        },
        "& .MuiInputBase-input::placeholder": {
          color: isDark ? "#6b7280" : "#9ca3af",
        },
        ...props.sx,
      }}
    />
  );
};

export default function SignUpForm() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const fileRef = React.useRef(null);
  const [errors, setErrors] = useState({});
  const [isDark, setIsDark] = useState(false);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobileNumber: "",
    alternativeContact: "",
    country: "",
    province: "",
    district: "",
    address: "",
    messageLanguage: "",
    profile_picture: "",
    status: "active", 
    accountType: "merchant",
    registrationType: "indirect", 
  });

  // Create MUI theme based on dark mode
  const muiTheme = createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: {
        main: '#CD0C02',
      },
      background: {
        default: isDark ? '#1f2937' : '#ffffff',
        paper: isDark ? '#1f2937' : '#ffffff',
      },
      text: {
        primary: isDark ? '#f3f4f6' : '#1f2937',
        secondary: isDark ? '#9ca3af' : '#6b7280',
      },
    },
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? '#4b5563' : '#e5e7eb',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#ff6666',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#CD0C02',
            },
          },
          input: {
            color: isDark ? '#f3f4f6' : '#1f2937',
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: isDark ? '#9ca3af' : '#6b7280',
            '&.Mui-focused': {
              color: '#CD0C02',
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            '& .MuiAutocomplete-inputRoot': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
            },
          },
          paper: {
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#f3f4f6' : '#1f2937',
          },
          option: {
            backgroundColor: isDark ? '#1f2937' : '#ffffff',
            color: isDark ? '#f3f4f6' : '#1f2937',
            '&:hover': {
              backgroundColor: isDark ? '#374151' : '#f3f4f6',
            },
          },
        },
      },
    },
  });

  // Dark mode detection with MutationObserver
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(isDarkModeActive());
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });
    
    observer.observe(document.documentElement, { attributes: true });
    
    return () => observer.disconnect();
  }, []);

  const { data: countries, isLoading: loadingCountries } = useQuery({
    queryKey: ["countries"],
    queryFn: getCoutries,
  });

  const { data: provinces, isLoading: loadingProvinces } = useQuery({
    queryKey: ["provinces", formData.country],
    queryFn: () => getProvincesBaseOnCountry(formData.country),
    enabled: !!formData.country,
  });

  const { data: districts, isLoading: loadingDistricts } = useQuery({
    queryKey: ["districts", formData.province],
    queryFn: () => getDistrictsBaseOnProvince(formData.province),
    enabled: !!formData.province,
  });

  const mutation = useMutation({
    mutationFn: (formData) => signUpAgent(formData),
    onSuccess: () => {
      toast.success(t('signUp.success.message') || "Sign up successful!");
      navigate("/signin");
    },
    onError: (error) => {
      console.error("Sign up error:", error);
      toast.error(error.response?.data?.error || t('signUp.errors.signupFailed') || "Failed to create agent account");
    },
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        handleChange("profile_picture", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName) newErrors.firstName = t('signUp.errors.firstNameRequired');
    if (!formData.lastName) newErrors.lastName = t('signUp.errors.lastNameRequired');
    if (!formData.email) newErrors.email = t('signUp.errors.emailRequired');
    if (!formData.mobileNumber) newErrors.mobileNumber = t('signUp.errors.phoneRequired');
    if (!formData.country) newErrors.country = t('signUp.errors.countryRequired');
    if (!formData.province) newErrors.province = t('signUp.errors.provinceRequired');
    if (!formData.district) newErrors.district = t('signUp.errors.districtRequired');
    if (!formData.address) newErrors.address = t('signUp.errors.addressRequired');
    if (!formData.messageLanguage) newErrors.messageLanguage = t('signUp.errors.languageRequired');
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    const username = `${formData.firstName} ${formData.lastName}`;
    
    const payload = {
      ...formData,
      username,
      permissions: [], 
      user_type: "agent",
    };
    
    mutation.mutate(payload);
  };

  const toggleLanguage = () => {
    const languagesList = ['en', 'fa', ];
    const currentIndex = languagesList.indexOf(i18n.language);
    const nextIndex = (currentIndex + 1) % languagesList.length;
    i18n.changeLanguage(languagesList[nextIndex]);
  };

  const getLanguageButtonText = () => {
    if (i18n.language === 'en') return 'فارسی';
    if (i18n.language === 'fa') return 'English';
    return 'English';
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900' 
          : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
      }`}>
        <div className="absolute top-4 right-4 z-50">
          <button
            onClick={toggleLanguage}
            className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
              isDark 
                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md'
            }`}
          >
            {getLanguageButtonText()}
          </button>
        </div>

        <StyledForm onSubmit={handleSubmit}>
          <div className="text-center mb-8">
            <h2 className={`text-3xl font-bold transition-colors duration-300 ${
              isDark ? 'text-white' : 'text-gray-800'
            }`}>
              {t('signUp.title')}
            </h2>
            <p className={`mt-2 transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {t('signUp.subtitle')}
            </p>
          </div>

          <div className="flex justify-center mb-8">
            <div className="relative w-32 h-32 rounded-full border-2 border-dashed group"
                 style={{ borderColor: isDark ? '#4b5563' : '#d1d5db' }}>
              {formData.profile_picture ? (
                <img 
                  src={formData.profile_picture} 
                  className="w-full h-full rounded-full object-cover transition-transform group-hover:scale-105"
                  alt="Profile"
                />
              ) : (
                <div className={`w-full h-full rounded-full flex items-center justify-center transition-colors ${
                  isDark 
                    ? 'bg-gray-700 group-hover:bg-gray-600' 
                    : 'bg-gray-200 group-hover:bg-gray-300'
                }`}>
                  <Camera className={isDark ? 'text-gray-400' : 'text-gray-500'} size={24} />
                </div>
              )}
              <button
                type="button"
                onClick={() => fileRef.current.click()}
                className={`absolute bottom-0 right-0 rounded-full p-2 shadow-md transition-colors ${
                  isDark 
                    ? 'bg-gray-700 hover:bg-gray-600' 
                    : 'bg-white hover:bg-gray-100'
                }`}
              >
                <Camera size={20} className={isDark ? 'text-gray-300' : 'text-gray-600'} />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </button>
            </div>
          </div>

          <FormSection>
            <SectionTitle>{t('signUp.personalInfo')}</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomTextField
                label={t('signUp.firstName')}
                variant="outlined"
                fullWidth
                isDark={isDark}
                value={formData.firstName}
                onChange={(e) => handleChange("firstName", e.target.value)}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
              <CustomTextField
                label={t('signUp.lastName')}
                variant="outlined"
                fullWidth
                isDark={isDark}
                value={formData.lastName}
                onChange={(e) => handleChange("lastName", e.target.value)}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
              <CustomTextField
                label={t('signUp.email')}
                type="email"
                variant="outlined"
                fullWidth
                isDark={isDark}
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
              />
              <CustomTextField
                label={t('signUp.phoneNumber')}
                variant="outlined"
                fullWidth
                isDark={isDark}
                value={formData.mobileNumber}
                onChange={(e) => handleChange("mobileNumber", e.target.value)}
                error={!!errors.mobileNumber}
                helperText={errors.mobileNumber}
              />
              <CustomTextField
                label={t('signUp.alternativeContact')}
                variant="outlined"
                fullWidth
                isDark={isDark}
                value={formData.alternativeContact}
                onChange={(e) => handleChange("alternativeContact", e.target.value)}
              />
            </div>
          </FormSection>

          <FormSection>
            <SectionTitle>{t('signUp.locationInfo')}</SectionTitle>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Autocomplete
                options={countries?.data || []}
                getOptionLabel={(option) => option.countryName}
                loading={loadingCountries}
                onChange={(_, newValue) => handleChange("country", newValue?.id || "")}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label={t('signUp.country')}
                    isDark={isDark}
                    error={!!errors.country}
                    helperText={errors.country}
                  />
                )}
              />
              
              <Autocomplete
                options={provinces?.data || []}
                getOptionLabel={(option) => option.provinceName}
                loading={loadingProvinces}
                disabled={!formData.country}
                onChange={(_, newValue) => handleChange("province", newValue?.id || "")}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label={t('signUp.province')}
                    isDark={isDark}
                    error={!!errors.province}
                    helperText={errors.province}
                  />
                )}
              />
              
              <Autocomplete
                options={districts?.data || []}
                getOptionLabel={(option) => option.districtName}
                loading={loadingDistricts}
                disabled={!formData.province}
                onChange={(_, newValue) => handleChange("district", newValue?.id || "")}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label={t('signUp.district')}
                    isDark={isDark}
                    error={!!errors.district}
                    helperText={errors.district}
                  />
                )}
              />
              
              <div className="md:col-span-3">
                <CustomTextField
                  label={t('signUp.fullAddress')}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  isDark={isDark}
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </div>
            </div>
          </FormSection>

          <FormSection>
            <SectionTitle>{t('signUp.preferences')}</SectionTitle>
            <div className="grid grid-cols-1 gap-4">
              <Autocomplete
                options={languages}
                getOptionLabel={(option) => option.label}
                onChange={(_, newValue) => handleChange("messageLanguage", newValue?.value || "")}
                renderOption={(props, option) => (
                  <Box 
                    component="li" 
                    {...props}
                  >
                    <Avatar 
                      src={option.icon} 
                      sx={{ width: 24, height: 24, mr: 1 }} 
                    />
                    {option.label}
                  </Box>
                )}
                renderInput={(params) => (
                  <CustomTextField
                    {...params}
                    label={t('signUp.preferredLanguage')}
                    isDark={isDark}
                    error={!!errors.messageLanguage}
                    helperText={errors.messageLanguage}
                  />
                )}
              />
            </div>
          </FormSection>

          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={mutation.isLoading}
              sx={{
                minWidth: 200,
                padding: "12px 24px",
                fontSize: "1rem",
                fontWeight: 600,
                background: 'linear-gradient(45deg, #CD0C02 30%, #EC68B5 90%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #B10202 30%, #D95CA0 90%)',
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              {mutation.isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('signUp.createAccount')
              )}
            </Button>
          </div>

          <div className="mt-4 text-center">
            <Button 
              onClick={() => navigate("/signin")} 
              color="secondary"
              sx={{
                color: "#CD0C02",
                fontWeight: 500,
                '&:hover': {
                  textDecoration: "underline",
                  backgroundColor: "transparent",
                }
              }}
            >
              {t('signUp.alreadyHaveAccount')}
            </Button>
          </div>
        </StyledForm>
      </div>
    </ThemeProvider>
  );
}